import {
	useQueries,
	useQuery,
	useQueryErrorResetBoundary,
} from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import {
	AlertCircle,
	Building07,
	ChevronDown,
	Send02,
	SlashCircle01,
} from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import {
	DetailField,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { OrganizationCard } from "@/components/base/organization-card";
import { TabHeader } from "@/components/base/tab-header";
import { EmptyValue, PhoneNumber } from "@/components/common";
import { createOptionalPhoneNumberSchema } from "@/components/form/fields/PhoneField.validators";
import { ConfirmationModal } from "@/components/Organisms/ConfirmationModal/ConfirmationModal";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useEditOrgUserOrgsForm } from "@/forms/user/organizationUser/orgs/edit-org-user-orgs.form.hooks";
import { useIndexedValidActions } from "@/hooks/use-indexed-valid-actions";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import {
	ORGANIZATION_STATUS,
	organizationQueryOptions,
} from "@/services/organization";
import {
	ORGANIZATION_USER_STATUS,
	USER_INVITE_STATUS,
	userQueryOptions,
} from "@/services/user";
import { type UserInvite, userInviteQueries } from "@/services/user-invite";
import {
	useResendInvitation,
	useRevokeInvitation,
} from "@/services/user-invite/user-invite.mutations";
import { cx } from "@/utils/cx";
import { SearchSchema, statusColors } from "../$userId";
import { ActiveIcon, StatusCircle, SuspendedIcon } from "./details";
// 🧪 TESTING: Uncomment to use mock data
// import { getMockUserByScenario } from "./organizations.mock";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/organizations",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const [isResendOpen, setIsResendOpen] = useState(false);
	const [isRevokeOpen, setIsRevokeOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<{
		type: "resend" | "revoke";
		userId?: string;
		inviteId?: string;
		organizationId?: string;
	} | null>(null);

	const navigate = useNavigate();
	const _queryErrorResetBoundary = useQueryErrorResetBoundary();
	const _router = useRouter();
	void _queryErrorResetBoundary;
	void _router;
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/organizations",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/organizations",
	});

	const revokeInvitation = useRevokeInvitation();
	const resendInvitation = useResendInvitation();

	const _handleClose = () => {
		navigate({
			to: "/admin/users/organization/$userId/details",
			params: { userId: params.userId },
			search: { mode: "view" },
		});
	};
	void _handleClose;

	// Modal handlers
	const handleResendInvitation = async () => {
		console.log("Resending invitation for:", pendingAction);
		// Add your resend invitation mutation here
		resendInvitation.mutate(pendingAction?.inviteId ?? "");
		setIsResendOpen(false);
		setPendingAction(null);
	};

	const handleRevokeAccess = async () => {
		console.log("Revoking access for:", pendingAction);
		revokeInvitation.mutate(pendingAction?.inviteId ?? "");
		// Add your revoke access mutation here
		setIsRevokeOpen(false);
		setPendingAction(null);
	};

	// Create a ref map to store refs for each organization card
	const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
	// Create a ref for the scrollable container
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	// Track if scroll should be locked
	// const [isScrollLocked, setIsScrollLocked] = useState(false);

	const isScrollLocked = useMemo(() => {
		return search.mode === "edit" && search.organizationId;
	}, [search.mode, search.organizationId]);

	const editOrgOrgsForm = useEditOrgUserOrgsForm(params.userId, () => {
		// Success callback - navigate back to view
		navigate({
			to: "/admin/users/organization/$userId/organizations",
			params: { userId: params.userId },
			search: { mode: "view", organizationId: undefined },
		});
	});

	const userById = useQuery(userQueryOptions.detail(params.userId));
	const memberships = userById.data?.organizationUserInfo ?? [];
	const membershipEditChecks = useIndexedValidActions(
		memberships.map((membership) => ({
			name: "UpdateOrganizationUserInfo",
			entityType: "OrganizationUser",
			entityId: membership.id ?? "",
		})),
		memberships.length > 0,
	);
	// const organizationUserDetail = useQuery(organizationQueryOptions.users.detail(search.organizationId ?? "", "34204acd-df2d-42c8-a784-0503abd56d21"));
	const userInvitesByUserId = useQuery(
		userInviteQueries.invites.list(1, 10, { UserId: params.userId }),
	);

	const canResendUserInvite = useQuery(
		authQueryOptions.checkAction({
			name: "ResendUserInvite",
			entityType: "UserInvite",
			entityId: userInvitesByUserId.data?.[0]?.id,
		}),
	);
	const canRevokeUserInvite = useQuery(
		authQueryOptions.checkAction({
			name: "RevokeUserInvite",
			entityType: "User",
			entityId: userInvitesByUserId.data?.[0]?.id,
		}),
	);

	// Scroll the edited card into view when entering edit mode
	useEffect(() => {
		if (search.mode === "edit" && search.organizationId) {
			const cardElement = cardRefs.current.get(search.organizationId);
			if (cardElement) {
				// Use scrollIntoView with smooth behavior and align to start (top)
				cardElement.scrollIntoView({
					behavior: "smooth",
					block: "start",
					inline: "nearest",
				});

				// Lock scroll after animation completes (smooth scroll takes ~500ms)
				// setTimeout(() => {
				// setIsScrollLocked(true);
				// }, 600);
			}
		} else {
			// Unlock scroll when exiting edit mode
			// setIsScrollLocked(false);
		}
	}, [search.mode, search.organizationId]);

	useEffect(() => {
		if (
			search.mode !== "edit" ||
			!search.organizationId ||
			userById.isPending ||
			membershipEditChecks.isPending
		) {
			return;
		}
		const index = memberships.findIndex(
			(membership) => membership.organization?.id === search.organizationId,
		);
		if (index < 0 || membershipEditChecks.allowed[index] !== true) {
			navigate({
				to: "/admin/users/organization/$userId/organizations",
				params: { userId: params.userId },
				search: {
					mode: "view",
					organizationId: undefined,
					tab: "organizations",
				},
				replace: true,
			});
		}
	}, [
		membershipEditChecks.allowed,
		membershipEditChecks.isPending,
		memberships,
		navigate,
		params.userId,
		search.mode,
		search.organizationId,
		userById.isPending,
	]);

	// 🧪 TESTING: Uncomment to use mock data for testing
	// const userData = getMockUserByScenario("full");
	// Then replace all instances of `userById.data` below with `userData`

	// Collect all organization-related data
	const hasOrganization = memberships.some(
		(organization) => organization.organization !== null,
	);
	const hasSubOrganization = memberships.some(
		(organization) => organization.subOrganization !== null,
	);
	const hasSubContractor = memberships.some(
		(organization) => organization.subContractor !== null,
	);
	const hasAnyOrgData =
		hasOrganization || hasSubOrganization || hasSubContractor;

	// Fetch organization roles for all organizations at the top level
	// This avoids calling useQuery inside a .map() which violates Rules of Hooks

	const organizationUserDetailQueries = useQueries({
		queries:
			memberships.map((organization) => ({
				...organizationQueryOptions.users.detail(
					organization.organization?.id ?? "",
					organization.id ?? "",
				),
			})) ?? [],
	});

	const organizationRolesQueries = useQueries({
		queries:
			memberships.map((organization) => ({
				...organizationQueryOptions.roles.list(
					organization.organization?.id ?? "",
					1,
					10,
				),
			})) ?? [],
	});

	const subOrganizationQueries = useQueries({
		queries:
			memberships.map((organization) => ({
				...organizationQueryOptions.subOrganizations.list(
					organization.organization?.id ?? "",
					1,
					10,
				),
			})) ?? [],
	});

	const subContractorQueries = useQueries({
		queries:
			memberships.map((organization) => ({
				...organizationQueryOptions.subContractors.list(
					organization.organization?.id ?? "",
					1,
					10,
				),
			})) ?? [],
	});

	const colorMap = {
		Active: "success" as const,
		Inactive: "gray" as const,
		Suspended: "error" as const,
	} as const;

	const statusBadgeColorMap: Record<
		string,
		"success" | "warning" | "gray" | "error" | "blue"
	> = {
		Active: "success",
		Suspended: "warning",
		Archived: "gray",
		Retired: "error",
		Pending: "blue",
	};

	const statusTranslations = {
		Active: t("admin.users.tabs.organization.details.statuses.active"),
		Inactive: t("admin.users.tabs.organization.details.statuses.inactive"),
		Suspended: t("admin.users.tabs.organization.details.statuses.suspended"),
	} as const;

	return (
		<editOrgOrgsForm.AppForm>
			<div className="flex flex-col max-h-[calc(100vh-250px)] overflow-hidden">
				{/* Sticky Header */}
				<div className="shrink-0 pb-4">
					<TabHeader
						title={t(
							"admin.users.tabs.organization.details.organizations.title",
						)}
					/>
				</div>

				{/* Scrollable Content */}
				<div
					ref={scrollContainerRef}
					className={cx(
						"flex-1 min-h-0",
						isScrollLocked ? "overflow-hidden" : "overflow-y-auto",
					)}
				>
					{/*? Loading state */}
					{userById.isLoading && (
						<div className="flex items-center justify-center h-full">
							<LoadingIndicator type="line-simple" size="xl" supportingText />
						</div>
					)}

					{/*? Error state */}
					{userById.isError && (
						<div className="flex flex-col items-center justify-center h-full gap-4">
							<Illustration type="cloud" size="sm">
								<div className="bg-error-500 rounded-full p-3">
									<AlertCircle className="size-6" />
								</div>
							</Illustration>
							<div className="flex flex-col items-center justify-center pt-4">
								<p className="text-gray-900 font-semibold text-md">
									{t(
										"admin.users.tabs.organization.details.organizations.error",
									)}
								</p>
								<div className="w-[60%]">
									<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
										{t(
											"admin.users.tabs.organization.details.organizations.errorDescription",
										)}
									</p>
								</div>
							</div>
							{/* <div className="flex gap-3 justify-center">
								<Button color="secondary" size="md" onClick={handleClose}>
									{t("common.actions.cancel")}
								</Button>
								<Button
									color="primary"
									size="md"
									onClick={() => {
										// Reset the error boundary
										queryErrorResetBoundary.reset();
										// Invalidate the route to reload loaders
										router.invalidate();
									}}
								>
									{t("common.actions.retry")}
								</Button> */}
							{/* </div> */}
						</div>
					)}

					{/*? Empty state */}
					{!userById.isLoading && !userById.isError && !hasAnyOrgData && (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<Building07 className="w-12 h-12 text-gray-400 mb-3" />
							<h3 className="text-sm font-medium text-gray-900 mb-1">
								{t("admin.users.tabs.organization.details.organizations.empty")}
							</h3>
							<p className="text-sm text-gray-500">
								{t(
									"admin.users.tabs.organization.details.organizations.emptyDescription",
								)}
							</p>
						</div>
					)}

					{!userById.isLoading && !userById.isError && hasAnyOrgData && (
						<div
							className={cx(
								"space-y-6",
								search.mode === "edit" && "pb-[calc(100vh-200px)]",
							)}
						>
							{/* Primary Organization */}
							{hasOrganization &&
								// map through organizationUserInfo and return the organization card for each one
								memberships.map(
									(organization, index) => {
										const canEditMembership =
											membershipEditChecks.allowed[index] === true;
										const isEditingThisOrg =
											canEditMembership &&
											search.mode === "edit" &&
											search.organizationId === organization.organization?.id;

										const isEditDisabled =
											search.mode === "edit" &&
											search.organizationId !== organization.organization?.id &&
											organization.status?.code !== ORGANIZATION_STATUS.ACTIVE;

										const organizationUserDetail =
											organizationUserDetailQueries[index];

										// Find the pending invite for this organization
										const userInvite = userInvitesByUserId.data?.find(
											(invite: UserInvite) =>
												invite.organizationId === organization.organization?.id,
										);

										// Get the corresponding organization roles from the queries array
										const organizationRoles = organizationRolesQueries[index];
										const _subOrganization = subOrganizationQueries[index];
										const _subContractor = subContractorQueries[index];
										void _subOrganization;
										void _subContractor;

										return (
											<div
												key={organization.organization?.id ?? ""}
												ref={(el) => {
													if (el) {
														cardRefs.current.set(
															organization.organization?.id ?? "",
															el,
														);
													} else {
														cardRefs.current.delete(
															organization.organization?.id ?? "",
														);
													}
												}}
											>
												<OrganizationCard
													organizationId={organization.organization?.id ?? ""}
													title={
														organization.organization?.name || <EmptyValue />
													}
													showInvitationFooter={
														organization.status?.code !==
															ORGANIZATION_USER_STATUS.ACTIVE &&
														userInvite?.statusCode !==
															USER_INVITE_STATUS.ACCEPTED
													}
													isEditing={isEditingThisOrg}
													isEditDisabled={isEditDisabled}
													showEditButton={canEditMembership}
													invitationStatus={userInvite?.statusCode}
													onEdit={
														canEditMembership
															? () =>
																	navigate({
																		to: "/admin/users/organization/$userId/organizations",
																		params: {
																			userId: params.userId,
																		},
																		search: {
																			mode: "edit",
																			organizationId:
																				organization.organization?.id ?? "",
																			tab: "organizations",
																		},
																	})
															: undefined
													}
													onRevoke={
														canRevokeUserInvite.data
															? () => {
																	setIsRevokeOpen(true);
																	setPendingAction({
																		type: "revoke",
																		userId: organization.id ?? "",
																		inviteId: userInvite?.id ?? "",
																	});
																}
															: undefined
													}
													onResend={
														canResendUserInvite.data
															? () => {
																	setIsResendOpen(true);
																	setPendingAction({
																		type: "resend",
																		userId: organization.id ?? "",
																		inviteId: userInvite?.id ?? "",
																	});
																}
															: undefined
													}
													onCancel={() => {
														navigate({
															to: "/admin/users/organization/$userId/organizations",
															params: { userId: params.userId },
															search: {
																mode: "view",
																organizationId: undefined,
																tab: "organizations",
															},
														});
													}}
													onSave={() => editOrgOrgsForm.handleSubmit()}
													saveButton={
														<editOrgOrgsForm.Subscribe
															selector={(s) => [s.canSubmit, s.isSubmitting]}
														>
															{([canSubmit, isSubmitting]) => (
																<Button
																	color="primary"
																	size="md"
																	isLoading={isSubmitting}
																	isDisabled={!canSubmit || isSubmitting}
																	onClick={() => {
																		editOrgOrgsForm.handleSubmit();
																	}}
																>
																	{t("common.actions.save")}
																</Button>
															)}
														</editOrgOrgsForm.Subscribe>
													}
													cancelButton={
														<editOrgOrgsForm.Subscribe
															selector={(s) => [
																s.canSubmit,
																s.isDirty,
																s.isSubmitting,
															]}
														>
															{() => (
																<Button
																	color="secondary"
																	size="md"
																	onClick={() => {
																		navigate({
																			to: "/admin/users/organization/$userId/organizations",
																			params: { userId: params.userId },
																			search: {
																				mode: "view",
																				organizationId: undefined,
																				tab: "organizations",
																			},
																		});
																		editOrgOrgsForm.reset();
																	}}
																>
																	{t("common.actions.cancel")}
																</Button>
															)}
														</editOrgOrgsForm.Subscribe>
													}
												>
													<DetailFieldSection>
														<DetailField
															label={t("OrganizationUser.Status", {
																useFieldInfo: true,
															}).toUpperCase()}
															value={
																<BadgeWithDot
																	type="modern"
																	size="sm"
																	color={
																		statusBadgeColorMap[
																			organization.status.name ?? ""
																		] ?? "gray"
																	}
																>
																	{organization.status.name || <EmptyValue />}
																</BadgeWithDot>
															}
															isEditMode={isEditingThisOrg}
															editComponent={
																<editOrgOrgsForm.Field name="status">
																	{(field: any) => {
																		const currentValue =
																			field.state.value ||
																			organization.status?.name;
																		return (
																			<div className="flex flex-col gap-2">
																				<Dropdown.Root>
																					<label
																						htmlFor={t(
																							"OrganizationUser.Status",
																							{ useFieldInfo: true },
																						)}
																						className="text-sm font-medium text-gray-700"
																					>
																						{t("OrganizationUser.Status", {
																							useFieldInfo: true,
																						})}
																					</label>
																					<AriaButton
																						className={({
																							isPressed,
																							isFocused,
																						}) =>
																							cx(
																								`flex w-[50%] items-center justify-between gap-2 rounded-md bg-primary px-3 py-2.5 text-left text-sm h-full shadow-xs ring-1 ring-primary ring-inset transition-shadow duration-100 ease-linear`,
																								(isPressed || isFocused) &&
																									"!ring-2 !ring-brand",
																							)
																						}
																					>
																						<span
																							className={`flex items-center gap-2 font-semibold ${statusColors[(field.state.value || userById.data?.status?.name) as keyof typeof statusColors] || "text-gray-600"}`}
																						>
																							<StatusCircle
																								color={
																									currentValue === "Active"
																										? "success"
																										: currentValue ===
																												"Suspended"
																											? "error"
																											: currentValue ===
																													"Archived"
																												? "gray"
																												: currentValue ===
																														"Retired"
																													? "warning"
																													: "gray"
																								}
																							/>
																							{statusTranslations[
																								currentValue as keyof typeof statusTranslations
																							] || currentValue}
																						</span>
																						<ChevronDown className="size-4 text-gray-500" />
																					</AriaButton>
																					<Dropdown.Popover
																						placement="bottom left"
																						className="w-(--trigger-width)"
																					>
																						<Dropdown.Menu
																							aria-label={t(
																								"OrganizationUser.Status",
																								{ useFieldInfo: true },
																							)}
																							selectedKeys={
																								currentValue
																									? [currentValue]
																									: []
																							}
																							onSelectionChange={(keys) => {
																								const selected = Array.from(
																									keys,
																								)[0] as string;
																								field.handleChange(selected);
																							}}
																						>
																							<Dropdown.Item
																								id={"Active"}
																								key="Active"
																								icon={ActiveIcon}
																							>
																								{() => (
																									<span
																										className={`font-semibold ${colorMap.Active}`}
																									>
																										{t(
																											"admin.users.tabs.organization.details.statuses.active",
																										)}
																									</span>
																								)}
																							</Dropdown.Item>
																							<Dropdown.Item
																								id={"Suspended"}
																								key="Suspended"
																								icon={SuspendedIcon}
																							>
																								{() => (
																									<span
																										className={`font-semibold ${colorMap.Suspended}`}
																									>
																										{t(
																											"admin.users.tabs.organization.details.statuses.suspended",
																										)}
																									</span>
																								)}
																							</Dropdown.Item>
																						</Dropdown.Menu>
																					</Dropdown.Popover>
																				</Dropdown.Root>
																			</div>
																		);
																	}}
																</editOrgOrgsForm.Field>
															}
														/>
														<DetailField
															label={t("User.DisplayName", {
																useFieldInfo: true,
															}).toUpperCase()}
															value={organization.displayName || <EmptyValue />}
															valueClassName="font-bold text-gray-900"
															isEditMode={isEditingThisOrg}
															editComponent={
																<editOrgOrgsForm.Field name="displayName">
																	{(field: any) => (
																		<div className="flex flex-col gap-2">
																			<Input
																				type="text"
																				label={t("User.DisplayName", {
																					useFieldInfo: true,
																				})}
																				name={field.name}
																				value={field.state.value}
																				onChange={(value: string) =>
																					field.handleChange(value)
																				}
																				onBlur={field.handleBlur}
																				isInvalid={
																					field.state.meta.isTouched &&
																					field.state.meta.errors.length > 0
																				}
																			/>
																			{field.state.meta.isTouched &&
																				field.state.meta.errors.length > 0 && (
																					<p className="text-sm text-error-600">
																						{field.state.meta.errors[0].message}
																					</p>
																				)}
																		</div>
																	)}
																</editOrgOrgsForm.Field>
															}
														/>
														<DetailField
															label={t("OrganizationUser.Phone", {
																useFieldInfo: true,
															}).toUpperCase()}
															value={
																organization.phone ? (
																	<PhoneNumber
																		phoneNumber={organization.phone}
																	/>
																) : (
																	<EmptyValue />
																)
															}
															valueClassName="font-bold text-gray-900"
															isEditMode={isEditingThisOrg}
															editComponent={
																// <editOrgOrgsForm.Field name="phone">
																// 	{(field: any) => (
																// 		<div className="flex flex-col gap-2">
																// 			<Input
																// 				type="tel"
																// 				label={t(
																// 					"admin.users.tabs.organization.details.organizations.card.phone",
																// 				)}
																// 				name={field.name}
																// 				value={field.state.value}
																// 				onChange={(value: string) =>
																// 					field.handleChange(value)
																// 				}
																// 				onBlur={field.handleBlur}
																// 				isInvalid={
																// 					field.state.meta.isTouched &&
																// 					field.state.meta.errors.length > 0
																// 				}
																// 			/>
																// 			{field.state.meta.isTouched &&
																// 				field.state.meta.errors.length > 0 && (
																// 					<p className="text-sm text-error-600">
																// 						{field.state.meta.errors[0].message}
																// 					</p>
																// 				)}
																// 		</div>
																// 	)}

																// </editOrgOrgsForm.Field>
																<editOrgOrgsForm.AppField
																	name="phone"
																	validators={{
																		onChange:
																			createOptionalPhoneNumberSchema(t),
																	}}
																>
																	{(field) => (
																		<div className="flex flex-col gap-2">
																			<field.PhoneField
																				field={field}
																				label={t("OrganizationUser.Phone", {
																					useFieldInfo: true,
																				})}
																			/>
																		</div>
																	)}
																</editOrgOrgsForm.AppField>
															}
														/>
													</DetailFieldSection>
													<DetailFieldSection>
														<DetailField
															label={t(
																"admin.users.tabs.organization.details.organizations.card.role",
															)}
															value={
																organizationUserDetail.data
																	?.organizationRoles[0]?.name || <EmptyValue />
															}
															valueClassName="font-bold text-gray-900"
															isEditMode={isEditingThisOrg}
															editComponent={
																<editOrgOrgsForm.Field name="role">
																	{(field: any) => {
																		// Get the currently selected role ID from the form state
																		const selectedRoleId = field.state.value;

																		// Find the selected role in the available roles
																		const selectedRole = selectedRoleId
																			? organizationRoles.data?.find(
																					(role) => role.id === selectedRoleId,
																				)
																			: null;

																		// Display the selected role name, or fall back to the original roles, or show placeholder
																		const displayValue =
																			selectedRole?.name ||
																			organizationUserDetail.data
																				?.organizationRoles?.[0]?.name ||
																			t(
																				"admin.users.tabs.organization.details.organizations.card.selectRole",
																			);

																		return (
																			<div className="flex flex-col gap-2">
																				<Dropdown.Root>
																					<label
																						htmlFor={t(
																							"admin.users.tabs.organization.details.organizations.card.role",
																						)}
																						className="text-sm font-medium text-gray-700"
																					>
																						{t(
																							"admin.users.tabs.organization.details.organizations.card.role",
																						)}
																					</label>
																					<AriaButton className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left border border-gray-300 rounded-md hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500">
																						{displayValue}
																						<ChevronDown className="size-4 text-gray-500" />
																					</AriaButton>
																					<Dropdown.Popover className="w-(--trigger-width)">
																						<Dropdown.Menu
																							aria-label={t(
																								"admin.users.tabs.organization.details.organizations.card.role",
																							)}
																						>
																							{organizationRoles.data?.map(
																								(role) => (
																									<Dropdown.Item
																										id={role.id}
																										key={role.id}
																										onAction={() => {
																											// Store the ID, not the name
																											field.handleChange(
																												role.id,
																											);
																										}}
																									>
																										{role.name}
																									</Dropdown.Item>
																								),
																							)}
																						</Dropdown.Menu>
																					</Dropdown.Popover>
																				</Dropdown.Root>
																			</div>
																		);
																	}}
																</editOrgOrgsForm.Field>
															}
														/>
														<DetailField
															label={t("OrganizationUser.Title", {
																useFieldInfo: true,
															}).toUpperCase()}
															value={organization.title || <EmptyValue />}
															valueClassName="font-bold text-gray-900"
															isEditMode={isEditingThisOrg}
															editComponent={
																<editOrgOrgsForm.Field name="title">
																	{(field: any) => (
																		<div className="flex flex-col gap-2">
																			<Input
																				type="text"
																				label={t("OrganizationUser.Title", {
																					useFieldInfo: true,
																				})}
																				name={field.name}
																				value={field.state.value}
																				onChange={(value: string) =>
																					field.handleChange(value)
																				}
																				onBlur={field.handleBlur}
																				isInvalid={
																					field.state.meta.isTouched &&
																					field.state.meta.errors.length > 0
																				}
																			/>
																			{field.state.meta.isTouched &&
																				field.state.meta.errors.length > 0 && (
																					<p className="text-sm text-error-600">
																						{field.state.meta.errors[0].message}
																					</p>
																				)}
																		</div>
																	)}
																</editOrgOrgsForm.Field>
															}
														/>
														<DetailField
															label={t(
																"admin.users.tabs.organization.details.organizations.card.belongsTo",
															)}
															value={
																organizationUserDetail.data?.belongsTo || (
																	<EmptyValue />
																)
															}
															valueClassName="font-bold text-gray-900"
														/>
													</DetailFieldSection>
												</OrganizationCard>
											</div>
										);
									},
								)}
						</div>
					)}
				</div>
			</div>

			{/* Modals */}
			<ConfirmationModal
				isOpen={isResendOpen}
				setIsOpen={setIsResendOpen}
				title={t(
					"admin.users.tabs.organization.details.organizations.resendInvitation.title",
				)}
				description={t(
					"admin.users.tabs.organization.details.organizations.resendInvitation.description",
				)}
				icon={() => <Send02 className="text-brand-500" size={32} />}
				iconBgColor="!bg-[var(--color-bg-brand-primary)]"
				confirmText={t(
					"admin.users.tabs.organization.details.organizations.resendInvitation.confirmText",
				)}
				cancelText={t("common.actions.cancel")}
				onConfirm={handleResendInvitation}
				onCancel={() => {
					setIsResendOpen(false);
					setPendingAction(null);
				}}
				confirmColor="primary"
			/>

			<ConfirmationModal
				isOpen={isRevokeOpen}
				setIsOpen={setIsRevokeOpen}
				title={t(
					"admin.users.tabs.organization.details.organizations.revokeInvitation.title",
				)}
				description={t(
					"admin.users.tabs.organization.details.organizations.revokeInvitation.description",
				)}
				icon={() => <SlashCircle01 className="text-brand-500" size={32} />}
				iconBgColor="!bg-[var(--color-bg-brand-primary)]"
				confirmText={t(
					"admin.users.tabs.organization.details.organizations.revokeInvitation.confirmText",
				)}
				cancelText={t("common.actions.cancel")}
				onConfirm={handleRevokeAccess}
				onCancel={() => {
					setIsRevokeOpen(false);
					setPendingAction(null);
				}}
				confirmColor="primary"
			/>
		</editOrgOrgsForm.AppForm>
	);
}
